import * as React from "react";
import { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { advancedDataLinks } from "~/modules/AdvancedData";
import { appAdsLinks } from "~/modules/AppAds";
import { pmpLinks } from "~/modules/Pmp";
import { auditLinks } from "~/modules/Audit";
import { biddersLinks } from "~/modules/Bidders";
import { businessIncentivesLinks } from "~/modules/BusinessIncentives";
import { clientsLinks } from "~/modules/Clients";
import { creativesLinks } from "~/modules/Creatives";
import { dashboardLinks } from "~/modules/Dashboard";
import { dictionaryLinks } from "~/modules/Dictionary";
import { paymentsLinks } from "~/modules/Payments/links";
import { profileLinks } from "~/modules/Profile";
import { useSession } from "~/modules/Profile/session";
import { rillAnalyticsLinks } from "~/modules/RillAnalytics";
import { featuresLinks } from "~/modules/Features";
import { sourcesLinks } from "~/modules/Sources/links";
import { usersLinks } from "~/modules/Users";
import { RootAbility } from "~/root_ability";
import {
  BarChart2,
  BookOpen,
  BadgePercent,
  Boxes,
  BriefcaseBusiness,
  CircleDollarSign,
  Cog,
  FileJson,
  FileText,
  Handshake,
  Info,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  MonitorPlay,
  SlidersHorizontal,
  Star,
  Table,
  Users,
} from "lucide";
import { LucideIcon } from "~/components/LucideIcon";
import style from "./index.module.scss";

interface ISidebarItemProps {
  icon: ReactNode;
  to: string;
  name: string;
  external?: boolean;
}

export const SidebarItem = (props: ISidebarItemProps) => {
  const body = [
    <div key="icon" className={style.sidebarItemIcon}>
      {props.icon}
    </div>,
    <div key="name" className={style.sidebarItemName}>
      {props.name}
    </div>,
  ];

  return props.external ? (
    <a href={props.to} target="_blank" className={style.sidebarItem}>
      {body}
    </a>
  ) : (
    <NavLink
      to={props.to}
      className={({ isActive }) =>
        [style.sidebarItem, isActive && style.sidebarItemActive]
          .filter(Boolean)
          .join(" ")
      }
    >
      {body}
    </NavLink>
  );
};

export const SidebarView = () => {
  const {
    session: { user },
  } = useSession();
  const ability = new RootAbility(user);
  const sellerName = ability.isSelfServe()
    ? "Source & Blockings"
    : ability.isSeller()
      ? "Source"
      : "";

  const canViewTopCreatives =
    !ability.isEasybrain() && (ability.isSeller() || ability.isSelfServe());

    const helpLink = 'https://developers.bidmachine.io/';

  return (
    <div className={style.sidebar}>
      <SidebarItem
        icon={<LucideIcon icon={LayoutDashboard} />}
        name="Dashboard"
        to={dashboardLinks.dashboard()}
      />
      {ability.canViewRillAnalytics() && (
        <SidebarItem
          icon={<LucideIcon icon={BarChart2} />}
          name="Analytics"
          to={rillAnalyticsLinks.rillAnalytics()}
        />
      )}
      {ability.canViewBidders() && (
        <SidebarItem
          icon={<LucideIcon icon={Handshake} />}
          name="Bidders"
          to={biddersLinks.bidders()}
        />
      )}
      {ability.canViewBusinessIncentives() && (
        <SidebarItem
          icon={<LucideIcon icon={BadgePercent} />}
          name="Expenses"
          to={businessIncentivesLinks.businessIncentives()}
        />
      )}
      {ability.canViewSources() && (
        <SidebarItem
          icon={<LucideIcon icon={Megaphone} />}
          name="Sources"
          to={sourcesLinks.sources()}
        />
      )}
      {ability.canViewSellerSettings() && (
        <SidebarItem
          icon={<LucideIcon icon={SlidersHorizontal} />}
          name={sellerName}
          to={clientsLinks.selfServeSeller()}
        />
      )}
      {ability.canViewPmp() && (
        <SidebarItem
          icon={<LucideIcon icon={ListChecks} />}
          name="PMP"
          to={pmpLinks.pmp()}
        />
      )}
      {ability.canViewPayments() && (
        <SidebarItem
          icon={<LucideIcon icon={CircleDollarSign} />}
          name="Payments"
          to={paymentsLinks.transactionHistory()}
        />
      )}
      {ability.canViewPaymentsTrolley() && (
        <SidebarItem
          icon={<LucideIcon icon={CircleDollarSign} />}
          name="Payments"
          to={paymentsLinks.paymentsTrolley()}
        />
      )}
      {ability.canViewAdvancedData() && (
        <SidebarItem
          icon={<LucideIcon icon={Boxes} />}
          name="Advanced Reporting"
          to={advancedDataLinks.advancedData()}
        />
      )}
      {ability.canViewCreatives() && (
        <SidebarItem
          icon={<LucideIcon icon={MonitorPlay} />}
          name="Creatives preview"
          to={creativesLinks.creatives()}
        />
      )}
      {canViewTopCreatives && (
        <SidebarItem
          icon={<LucideIcon icon={Star} />}
          name="Top creatives"
          to={creativesLinks.topPreview()}
        />
      )}
      {ability.canViewAppAds() && (
        <SidebarItem
          icon={<LucideIcon icon={FileText} />}
          to={
            ability.isSeller() || ability.isSelfServe()
              ? appAdsLinks.appAds(ability.sellerId())
              : appAdsLinks.main()
          }
          name={"app-ads.txt"}
        />
      )}
      {ability.canViewSellersJson() && (
        <SidebarItem
          icon={<LucideIcon icon={FileJson} />}
          name="sellers.json review"
          to={clientsLinks.sellersJson()}
        />
      )}
      {ability.canViewAudit() && (
        <SidebarItem
          icon={<LucideIcon icon={Table} />}
          name="Audit"
          to={auditLinks.audit()}
        />
      )}
      {ability.canViewClients() && (
        <SidebarItem
          icon={<LucideIcon icon={BriefcaseBusiness} />}
          name="Clients"
          to={clientsLinks.clients()}
        />
      )}
      {ability.canViewUsers() && (
        <SidebarItem
          icon={<LucideIcon icon={Users} />}
          name="Users"
          to={usersLinks.users()}
        />
      )}
      {ability.isAdmin() && (
        <SidebarItem
          icon={<LucideIcon icon={BookOpen} />}
          name="Dictionary"
          to={dictionaryLinks.dictionary()}
        />
      )}
      {ability.canViewApiTokens() && (
        <SidebarItem
          icon={<LucideIcon icon={KeyRound} />}
          name="API Tokens"
          to={profileLinks.apiTokens()}
        />
      )}
      {ability.canViewFeatures() && (
        <SidebarItem
          icon={<LucideIcon icon={Cog} />}
          name="Features"
          to={featuresLinks.features()}
        />
      )}
      <SidebarItem
        icon={<LucideIcon icon={Info} />}
        name="Help"
        external
        to={helpLink}
      />
    </div>
  );
};
